import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, MenuItem, TextField, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, CircularProgress, Card, CardContent, Stack, Typography
} from '@mui/material';
import PinDropIcon from '@mui/icons-material/PinDrop';
import axios from 'axios';
import ModuleHeader from './components/ModuleHeader';
import { useAuth } from './AuthContext';
import { API_URL } from './config';
import { proximaDataPE, fmtDataPE } from './utils/peDates';

interface Ciclo   { id: number; nome: string; ativo: boolean; }
interface CicloPe { id: number; numero: number; ano: number; ativo: boolean; encerrado: boolean; }
interface Area    { id: number; nome: string; zona: string; }
interface Agente  { id: number; nome: string; matricula: string; }

interface LinhaGeralPE {
  ciclo_pe_id: number | null;
  ciclo_pe_numero: number | null;
  se: number;
  data_inicio: string;
  data_fim: string;
  imoveis_informados: number;
  quarteirao_concluido: number;
  total_registros: number;
  imoveis_ponto_estrategico: number;
  total_imoveis: number;
  trat_focal: number;
  trat_perifocal: number;
  inspecionados: number;
  recuperados: number;
  recusados: number;
  fechados: number;
  amostras_coletadas: number;
  dep_a1: number; dep_a2: number; dep_b: number; dep_c: number;
  dep_d1: number; dep_d2: number; dep_e: number;
  dep_total: number;
  dep_eliminados: number;
  dep_tratados: number;
  larvicida_total_gramas: number;
  imoveis_positivos: number;
  dep_positivos: number;
  hdt: number;
  media_imoveis_agente: number;
}

interface Totais {
  imoveis_informados: number;
  quarteirao_concluido: number;
  total_registros: number;
  imoveis_ponto_estrategico: number;
  total_imoveis: number;
  trat_focal: number;
  trat_perifocal: number;
  inspecionados: number;
  recuperados: number;
  recusados: number;
  fechados: number;
  amostras_coletadas: number;
  dep_a1: number; dep_a2: number; dep_b: number; dep_c: number;
  dep_d1: number; dep_d2: number; dep_e: number;
  dep_total: number;
  dep_eliminados: number;
  dep_tratados: number;
  larvicida_total_gramas: number;
  imoveis_positivos: number;
  dep_positivos: number;
  hdt: number;
  media_imoveis_agente: number;
}

interface ApiData { linhas: LinhaGeralPE[]; totais: Totais; }

const fmtData = (s: string) => {
  const [a, m, d] = s.split('-');
  return `${d}/${m}/${a.slice(2)}`;
};

// ──── Paleta Designer (tema escuro) — idêntica ao ResumoGeral ────
const C = {
  bg:        '#030b16',
  bgPanel:   '#071323',
  bgRow:     '#0a1727',
  bgRowAlt:  '#0c1c30',
  navy:      '#0d1c35',
  cyan:      '#00b8cc',
  cyanDim:   'rgba(0,184,204,0.12)',
  cyanBorder:'rgba(0,184,204,0.22)',
  orange:    '#e85d26',
  white:     '#f0f4f8',
  gray:      '#7a8799',
  grayLt:    '#b0bcc8',
  border:    'rgba(255,255,255,0.07)',
  borderMid: 'rgba(255,255,255,0.18)',
  green:     '#22c55e',
  purple:    '#8b5cf6',
  amber:     '#f59e0b',
};

const FIELD_SX = {
  minWidth: 180,
  '& .MuiInputBase-root': { backgroundColor: '#0a1830', color: C.white, fontFamily: "'Inter', sans-serif" },
  '& .MuiInputBase-input': { color: C.white },
  '& .MuiInputLabel-root': { color: C.grayLt, fontFamily: "'Inter', sans-serif" },
  '& .MuiInputLabel-root.Mui-focused': { color: C.cyan },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.cyanBorder },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.cyan },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.cyan, borderWidth: '1px' },
  '& .MuiSelect-icon': { color: C.grayLt },
};

const PERIODO_CELL_SX = {
  bgcolor: '#0a1727',
  color: '#f59e0b',
  fontWeight: 700,
  fontSize: 14,
  fontFamily: "'Inter', sans-serif",
  borderRight: `2px solid ${C.cyanBorder}`,
};

// ──── COL_GROUPS adaptado para PE ────
// Diferença vs Resumo Geral: grupo IMÓVEIS TRABALHADOS só tem PE + TOTAL
const COL_GROUPS: { label: string; color: string; bodyColor: string; cols: { key: keyof LinhaGeralPE; label: string; fmt?: (v: any) => any }[] }[] = [
  { label: 'CICLO PE', color: C.purple, bodyColor: 'transparent', cols: [
    { key: 'ciclo_pe_numero', label: 'Ciclo', fmt: (v) => v != null ? `PE ${v}` : '—' },
  ]},
  { label: 'SEMANA EPIDEMIOLÓGICA', color: C.grayLt, bodyColor: 'transparent', cols: [
    { key: 'se',                   label: 'SE',      fmt: (v) => `SE ${v}` },
    { key: 'data_inicio',          label: 'Período', fmt: (v) => v },
    { key: 'imoveis_informados',   label: 'Im.Inf.' },
    { key: 'quarteirao_concluido', label: 'Qtd.C' },
    { key: 'total_registros',      label: 'FAD-01s' },
  ]},
  { label: 'IMÓVEIS TRABALHADOS', color: C.cyan, bodyColor: 'transparent', cols: [
    { key: 'imoveis_ponto_estrategico', label: 'P.Estrat' },
    { key: 'total_imoveis',             label: 'Total' },
  ]},
  { label: 'SITUAÇÃO DOS IMÓVEIS', color: C.amber, bodyColor: 'transparent', cols: [
    { key: 'trat_focal',     label: 'Tr.Focal' },
    { key: 'trat_perifocal', label: 'Tr.Perif.' },
    { key: 'inspecionados',  label: 'Inspec.' },
    { key: 'recuperados',    label: 'Recuper.' },
    { key: 'recusados',      label: 'Recusa' },
    { key: 'fechados',       label: 'Fechados' },
  ]},
  { label: 'AMOSTRAS', color: C.purple, bodyColor: 'transparent', cols: [
    { key: 'amostras_coletadas', label: 'Amostras' },
  ]},
  { label: 'DEPÓSITOS INSPECIONADOS', color: C.green, bodyColor: 'transparent', cols: [
    { key: 'dep_a1', label: 'A1' },
    { key: 'dep_a2', label: 'A2' },
    { key: 'dep_b',  label: 'B' },
    { key: 'dep_c',  label: 'C' },
    { key: 'dep_d1', label: 'D1' },
    { key: 'dep_d2', label: 'D2' },
    { key: 'dep_e',  label: 'E' },
    { key: 'dep_total', label: 'Total' },
    { key: 'dep_eliminados',          label: 'Elimin.' },
    { key: 'dep_tratados',            label: 'Trat.' },
  ]},
  { label: 'LARVICIDA L1+L2', color: C.orange, bodyColor: 'transparent', cols: [
    { key: 'larvicida_total_gramas', label: 'Gramas', fmt: (v) => v > 0 ? parseFloat(v).toFixed(1).replace('.', ',') + ' g' : '—' },
  ]},
  { label: 'POSITIVOS', color: C.orange, bodyColor: 'transparent', cols: [
    { key: 'imoveis_positivos',   label: 'Imóv.+' },
    { key: 'dep_positivos', label: 'Dep.+' },
  ]},
  { label: 'PRODUÇÃO', color: C.cyan, bodyColor: 'transparent', cols: [
    { key: 'hdt',                   label: 'HD.T' },
    { key: 'media_imoveis_agente',  label: 'Med.' },
  ]},
];

// ──── Componente TC (TableCell wrapper) ────
const TC = ({ children, align = 'center', sx = {}, ...rest }: any) => (
  <TableCell align={align} {...rest} sx={{
    px: 0.8,
    py: 0.9,
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
    fontFamily: "'Inter', sans-serif",
    fontVariantNumeric: 'tabular-nums',
    color: C.white,
    borderBottom: `2px solid ${C.borderMid}`,
    borderRight: `2px solid ${C.borderMid}`,
    ...sx,
  }}>{children}</TableCell>
);

interface Props { onVoltar: () => void; }

const PontoEstrategicoPE: React.FC<Props> = ({ onVoltar }) => {
  const [ciclos, setCiclos]       = useState<Ciclo[]>([]);
  const [ciclosPe, setCiclosPe]   = useState<CicloPe[]>([]);
  const [areas, setAreas]         = useState<Area[]>([]);
  const [agentes, setAgentes]     = useState<Agente[]>([]);
  const [cicloId, setCicloId]     = useState<number | ''>('');
  const [cicloPeId, setCicloPeId] = useState<number | ''>('');
  const [areaId, setAreaId]       = useState<number | ''>('');
  const [agenteId, setAgenteId]   = useState<number | ''>('');
  const [dados, setDados]         = useState<ApiData | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]             = useState('');
  const [dashCards, setDashCards]   = useState<any>(null);

  const { token } = useAuth();
  const headers = { Authorization: 'Bearer ' + token };

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/ciclos/`, { headers }),
      axios.get(`${API_URL}/api/ciclos-pe/`, { headers }),
      axios.get(`${API_URL}/api/areas/`, { headers }),
      axios.get(`${API_URL}/api/agentes/`, { headers }),
    ]).then(([rc, rcpe, ra, rag]) => {
      setCiclos(rc.data);
      setCiclosPe(rcpe.data);
      setAreas(ra.data.filter((a: Area) => (a as any).ativa !== false));
      setAgentes(rag.data.filter((a: Agente) => (a as any).status === 'ativo'));
      const cAtivo = rc.data.find((c: Ciclo) => c.ativo);
      if (cAtivo) setCicloId(cAtivo.id);
      const cpeAtivo = rcpe.data.find((c: CicloPe) => c.ativo);
      if (cpeAtivo) setCicloPeId(cpeAtivo.id);
    }).catch(() => setErro('Erro ao carregar listas.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buscar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const params: any = {};
      if (cicloId)   params.ciclo_id    = cicloId;
      if (cicloPeId) params.ciclo_pe_id = cicloPeId;
      if (areaId)    params.area_id     = areaId;
      if (agenteId)  params.agente_id   = agenteId;
      const r = await axios.get<ApiData>(`${API_URL}/api/pe/resumo-geral`, { headers, params });
      setDados(r.data);
    } catch {
      setErro('Erro ao carregar dados.');
    } finally {
      setCarregando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cicloId, cicloPeId, areaId, agenteId]);

  useEffect(() => { buscar(); }, [buscar]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/pe/dashboard-cards`, { headers })
      .then(r => setDashCards(r.data))
      .catch(() => setDashCards(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: C.bg, display: 'flex', flexDirection: 'column' }}>
      <ModuleHeader
        titulo="PE — Ponto Estratégico"
        subtitulo="Consolidado por SE e ciclo PE"
        icone={<PinDropIcon />}
        accentColor={C.cyan}
        onVoltar={onVoltar}
      />

      <Box sx={{ p: 3, flex: 1 }}>

        {/* ═══ Dashboard cards + legenda ═══ */}
        <Alert
          severity="info"
          icon={false}
          sx={{ mb: 2, bgcolor: C.bgPanel, color: C.grayLt, border: `1px solid ${C.cyanBorder}`, fontSize: '0.875rem' }}
        >
          <strong style={{ color: C.white }}>Áreas configuradas como Ponto Estratégico:</strong>{' '}
          {dashCards?.areas_pe?.length
            ? dashCards.areas_pe.map((a: any) => a.nome).join(', ')
            : '—'}
        </Alert>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          {/* Card 1: Último agente PE */}
          <Card sx={{ flex: 1, bgcolor: C.bgPanel, borderLeft: `4px solid ${C.cyan}`, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="overline" sx={{ color: C.gray, fontSize: '0.68rem', letterSpacing: '0.1em' }}>
                Último Agente PE
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, color: C.white, fontFamily: "'Inter', sans-serif" }}>
                {dashCards?.ultimo_agente?.agente_nome || '—'}
              </Typography>
              <Typography variant="body2" sx={{ color: C.grayLt }}>
                {dashCards?.ultimo_agente
                  ? `${dashCards.ultimo_agente.area_nome || '?'} · ${dashCards.ultimo_agente.data?.split('-').reverse().join('/') || ''}`
                  : 'Nenhum registro'}
              </Typography>
            </CardContent>
          </Card>

          {/* Card 2: Próximo ciclo PE agendado no banco */}
          <Card sx={{ flex: 1, bgcolor: C.bgPanel, borderLeft: `4px solid ${C.amber}`, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="overline" sx={{ color: C.gray, fontSize: '0.68rem', letterSpacing: '0.1em' }}>
                Próximo Ciclo PE
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, color: C.white, fontFamily: "'Inter', sans-serif" }}>
                {dashCards?.proximo_ciclo
                  ? `Ciclo ${dashCards.proximo_ciclo.numero}/${dashCards.proximo_ciclo.ano}`
                  : 'Não agendado'}
              </Typography>
              <Typography variant="body2" sx={{ color: C.grayLt }}>
                {dashCards?.proximo_ciclo?.data_inicio
                  ? `Início: ${dashCards.proximo_ciclo.data_inicio.split('-').reverse().join('/')}`
                  : 'Sem data prevista'}
              </Typography>
            </CardContent>
          </Card>

          {/* Card 3: Próximo PE no Calendário Epidemiológico */}
          <Card sx={{ flex: 1, bgcolor: C.bgPanel, borderLeft: `4px solid ${C.purple}`, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="overline" sx={{ color: C.gray, fontSize: '0.68rem', letterSpacing: '0.1em' }}>
                Próximo PE (Calendário Epi)
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, color: C.white, fontFamily: "'Inter', sans-serif" }}>
                {fmtDataPE(proximaDataPE())}
              </Typography>
              <Typography variant="body2" sx={{ color: C.grayLt }}>
                {(() => {
                  const prox = proximaDataPE();
                  if (!prox) return 'Sem PE futuro';
                  const dias = Math.ceil((prox.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                  if (dias <= 0) return 'Hoje!';
                  if (dias === 1) return 'Amanhã';
                  return `Em ${dias} dias`;
                })()}
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        {/* Filtros */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField select label="Ciclo" size="small" value={cicloId} onChange={e => setCicloId(Number(e.target.value) || '')} sx={FIELD_SX}>
            <MenuItem value="">Todos</MenuItem>
            {ciclos.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}{c.ativo ? ' ●' : ''}</MenuItem>)}
          </TextField>
          <TextField select label="Ciclo PE" size="small" value={cicloPeId} onChange={e => setCicloPeId(Number(e.target.value) || '')} sx={FIELD_SX}>
            <MenuItem value="">Todos</MenuItem>
            {ciclosPe.map(c => <MenuItem key={c.id} value={c.id}>PE {c.numero}/{c.ano}{c.ativo ? ' ●' : ''}</MenuItem>)}
          </TextField>
          <TextField select label="Área" size="small" value={areaId} onChange={e => setAreaId(Number(e.target.value) || '')} sx={FIELD_SX}>
            <MenuItem value="">Todas</MenuItem>
            {areas.map(a => <MenuItem key={a.id} value={a.id}>{a.nome}</MenuItem>)}
          </TextField>
          <TextField select label="Agente" size="small" value={agenteId} onChange={e => setAgenteId(Number(e.target.value) || '')} sx={FIELD_SX}>
            <MenuItem value="">Todos</MenuItem>
            {agentes.map(a => <MenuItem key={a.id} value={a.id}>{a.nome}</MenuItem>)}
          </TextField>
        </Box>

        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        {carregando && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: C.cyan }} /></Box>}
        {!carregando && dados && dados.linhas.length === 0 && (
          <Alert severity="info" sx={{ bgcolor: C.bgPanel, color: C.white, border: `1px solid ${C.cyanBorder}` }}>
            Nenhum lançamento PE encontrado para os filtros selecionados.
          </Alert>
        )}

        {dados && dados.linhas.length > 0 && !carregando && (
          <TableContainer component={Paper} elevation={0} sx={{
            borderRadius: 3.5,
            border: `1px solid ${C.border}`,
            overflow: 'auto',
            maxHeight: 'calc(100vh - 200px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.03)',
            background: `linear-gradient(180deg, ${C.bgPanel} 0%, #050e1c 100%)`,
          }}>
            <Table size="small" stickyHeader>
              <TableHead>
                {/* Linha 1: nomes dos grupos */}
                <TableRow>
                  {COL_GROUPS.map(g => (
                    <TableCell key={g.label} align="center" colSpan={g.cols.length} sx={{
                      backgroundColor: '#0a1830',
                      backgroundImage: `linear-gradient(180deg, ${g.color}40 0%, ${g.color}1c 100%)`,
                      color: C.white,
                      fontWeight: 800,
                      fontSize: 10.5,
                      fontFamily: "'Inter', sans-serif",
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      borderBottom: `2px solid ${g.color}`,
                      borderRight: `3px solid ${g.color}66`,
                      py: 0.7,
                      px: 1.5,
                      textShadow: `0 0 8px ${g.color}55`,
                    }}>
                      {g.label}
                    </TableCell>
                  ))}
                </TableRow>
                {/* Linha 2: nomes das colunas */}
                <TableRow>
                  {COL_GROUPS.flatMap(g => g.cols.map(c => (
                    <TableCell key={c.key as string} align="center" sx={{
                      bgcolor: '#0a1830',
                      color: g.color,
                      fontWeight: 700,
                      fontSize: 10.5,
                      fontFamily: "'Inter', sans-serif",
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      px: 0.8,
                      py: 1.2,
                      borderBottom: `2px solid ${C.cyanBorder}`,
                      borderRight: `2px solid ${C.borderMid}`,
                    }}>
                      {c.label}
                    </TableCell>
                  )))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dados.linhas.map((l, idxLinha) => (
                  <TableRow key={l.se} sx={{
                    background: idxLinha % 2 === 0 ? C.bgRow : C.bgRowAlt,
                    '&:hover': { background: 'rgba(0,184,204,0.06)' },
                    transition: 'background 150ms ease',
                  }}>
                    {COL_GROUPS.map(g => g.cols.map(c => {
                      let valor: any;
                      if (c.key === 'data_inicio') {
                        valor = `${fmtData(l.data_inicio)} a ${fmtData(l.data_fim)}`;
                      } else {
                        valor = l[c.key as keyof LinhaGeralPE];
                      }
                      const display = c.fmt ? c.fmt(valor) : valor;
                      const isCicloPe = c.key === 'ciclo_pe_numero';
                      const isSE      = c.key === 'se';
                      const isPeriodo = c.key === 'data_inicio';
                      return (
                        <TC key={c.key as string} sx={{
                          ...(isCicloPe ? { fontWeight: 700, color: '#f59e0b', fontSize: 17, textAlign: 'center' as const, borderRight: `2px solid ${C.cyanBorder}` } : {}),
                          ...(isSE ? { fontWeight: 700, color: '#f59e0b', fontSize: 17, textAlign: 'left' as const, borderRight: `2px solid ${C.cyanBorder}` } : {}),
                          ...(isPeriodo ? PERIODO_CELL_SX : {}),
                        }}>
                          {display}
                        </TC>
                      );
                    }))}
                  </TableRow>
                ))}
                {/* Linha TOTAL */}
                <TableRow sx={{
                  background: '#0d2438',
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 2,
                  borderTop: `2px solid ${C.cyanBorder}`,
                  '& td': {
                    backgroundColor: '#0d2438',
                    color: C.cyan,
                    fontWeight: 800,
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '0.85rem',
                    letterSpacing: '0.04em',
                  }
                }}>
                  {COL_GROUPS.map(g => g.cols.map(c => {
                    let display: any = '';
                    if (c.key === 'ciclo_pe_numero') {
                      display = 'TOTAL';
                    } else if (c.key === 'se') {
                      display = '—';
                    } else if (c.key === 'data_inicio') {
                      display = '—';
                    } else {
                      const v = (dados.totais as any)[c.key];
                      display = c.fmt ? c.fmt(v) : (v ?? 0);
                    }
                    return (
                      <TC key={c.key as string} sx={{ textAlign: (c.key === 'ciclo_pe_numero' || c.key === 'se') ? 'left' as const : 'center' as const }}>
                        {display}
                      </TC>
                    );
                  }))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default PontoEstrategicoPE;
